import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Loader2,
  LockKeyhole,
  Music2,
  ShieldCheck,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, fmtPrice } from "../lib/api";

const initialForm = {
  audio_track_count: 1,
  project_title: "",
  genre: "",
  brief: "",
  reference_links: "",
  deadline: "",
  file_notes: "",
};

export default function ServiceBookingDrawer({ engineer, service, preview, onClose }) {
  const { isCustomer } = useAuth();
  const location = useLocation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [previewComplete, setPreviewComplete] = useState(false);
  const [requestComplete, setRequestComplete] = useState(false);
  const [paymentMode, setPaymentMode] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/marketplace/payment-mode")
      .then(({ data }) => { if (active) setPaymentMode(data.mode); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!engineer || !service) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (preview) {
      setPreviewComplete(true);
      return;
    }
    if (!isCustomer) return;
    setSubmitting(true);
    try {
      await api.post("/service-orders", {
        engineer_id: engineer.id,
        service_id: service.id,
        audio_track_count: Number(form.audio_track_count),
        project_title: form.project_title,
        genre: form.genre,
        brief: form.brief,
        reference_links: form.reference_links.split("\n").map((item) => item.trim()).filter(Boolean),
        deadline: form.deadline,
        file_notes: form.file_notes,
      });
      setRequestComplete(true);
    } catch (error) {
      toast.error(error.userMessage || "Pesanan belum dapat diproses");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Service booking form">
      <button className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-[#09090b] border-l border-white/10 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 px-6 md:px-8 h-20 flex items-center justify-between bg-[#09090b]/90 backdrop-blur-xl border-b border-white/10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#e11d48]">Project Booking</div>
            <div className="font-[Outfit] text-lg font-bold mt-1">{engineer.name}</div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full border border-white/10 hover:bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 md:p-8">
          <div className="rounded-2xl border border-[#e11d48]/30 bg-[#e11d48]/5 p-5 flex items-start justify-between gap-5">
            <div><div className="text-sm font-bold">{service.name}</div><div className="text-xs text-zinc-500 mt-1">{service.description}</div><div className="text-[10px] text-zinc-600 mt-3">{service.turnaround_days || service.days} days · {service.revisions}× revisions{service.max_audio_tracks ? ` · Max. ${service.max_audio_tracks} audio tracks` : ""}</div></div>
            <div className="font-[Outfit] text-xl font-bold whitespace-nowrap">{fmtPrice(service.price)}</div>
          </div>

          {!isCustomer && !preview ? (
            <div className="py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#e11d48]/10 mx-auto flex items-center justify-center mb-5"><LockKeyhole className="w-6 h-6 text-[#e11d48]" /></div>
              <h2 className="font-[Outfit] text-2xl font-bold">Sign in to Book</h2>
              <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">Profiles and portfolios are public, but booking is only available to registered customers.</p>
              <div className="flex justify-center gap-3 mt-7">
                <Link to="/login" state={{ from: location.pathname }} className="px-5 py-3 rounded-full bg-[#e11d48] font-semibold text-sm">Sign In</Link>
                <Link to="/register" state={{ from: location.pathname }} className="px-5 py-3 rounded-full border border-white/10 font-semibold text-sm">Create Account</Link>
              </div>
            </div>
          ) : requestComplete ? (
            <div className="py-14 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-400/10 mx-auto flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8 text-emerald-400" /></div>
              <h2 className="font-[Outfit] text-2xl font-bold">Booking Request Sent</h2>
              <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">The engineer will review your brief. Once accepted, you can pay via DOKU from the order page and will be notified by email.</p>
              <Link to="/dashboard/services" className="inline-flex mt-7 px-6 py-3 rounded-full bg-[#e11d48] font-semibold text-sm">View My Orders</Link>
            </div>
          ) : previewComplete ? (
            <div className="py-14 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-400/10 mx-auto flex items-center justify-center mb-5"><CheckCircle2 className="w-8 h-8 text-emerald-400" /></div>
              <h2 className="font-[Outfit] text-2xl font-bold">Booking Preview Complete</h2>
              <p className="text-sm text-zinc-500 mt-2">In live mode, the request is sent to the engineer. DOKU payment becomes available after the engineer accepts it.</p>
              <button onClick={onClose} className="mt-7 px-6 py-3 rounded-full bg-white text-black font-semibold text-sm">Back to Profile</button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-6">
              {preview && <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-xs text-amber-200">Mode preview: formulir tidak membuat transaksi atau menghubungi DOKU.</div>}
              <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">Project Audio Track Count</span><input required type="number" min="1" max={service.max_audio_tracks || undefined} value={form.audio_track_count} onChange={(event) => setForm({ ...form, audio_track_count: event.target.value })} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e11d48]/70" /><span className="block text-[10px] text-zinc-500 mt-2">{service.max_audio_tracks ? `This package includes up to ${service.max_audio_tracks} audio tracks/stems.` : "The engineer will confirm your project track scope."} This count is provided by the customer when booking.</span></label>
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">Project Title</span><div className="relative"><Music2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" /><input required value={form.project_title} onChange={(event) => setForm({ ...form, project_title: event.target.value })} className="w-full bg-[#050505] border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#e11d48]/70" placeholder="My latest single" /></div></label>
                <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">Genre</span><input value={form.genre} onChange={(event) => setForm({ ...form, genre: event.target.value })} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e11d48]/70" placeholder="Pop / R&B" /></label>
              </div>
              <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">Project Brief</span><textarea required minLength="20" value={form.brief} onChange={(event) => setForm({ ...form, brief: event.target.value })} className="w-full min-h-36 bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-[#e11d48]/70 resize-y" placeholder="Describe the sound, file condition, and result you want..." /></label>
              <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">Reference Links</span><textarea value={form.reference_links} onChange={(event) => setForm({ ...form, reference_links: event.target.value })} className="w-full min-h-24 bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e11d48]/70 resize-y" placeholder="One link per line" /></label>
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">Target Completion Date</span><input type="date" value={form.deadline} onChange={(event) => setForm({ ...form, deadline: event.target.value })} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e11d48]/70" /></label>
                <label className="block"><span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500 mb-2">File Notes</span><input value={form.file_notes} onChange={(event) => setForm({ ...form, file_notes: event.target.value })} className="w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#e11d48]/70" placeholder="24-bit WAV, 48 kHz..." /></label>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#050505] p-5 space-y-3">
                <div className="text-xs text-zinc-300">The engineer will accept or reject your request before payment becomes available.</div>
                <div className="flex items-start gap-3"><ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5" /><div><div className="text-xs font-semibold">Payment via DOKU</div><div className="text-[10px] text-zinc-600 mt-1">{paymentMode === "doku_split" ? "DOKU holds the settlement and splits it after you approve the final result." : paymentMode === "manual_payout" ? "After you approve the final result, the admin manually transfers the engineer&apos;s share." : "The engineer payout method will appear once payment settings are loaded."}</div></div></div>
                <div className="flex items-start gap-3"><FileText className="w-4 h-4 text-sky-400 mt-0.5" /><div><div className="text-xs font-semibold">Price Locked to Order</div><div className="text-[10px] text-zinc-600 mt-1">Future price changes will not affect this order.</div></div></div>
              </div>

              <button disabled={submitting} className="w-full py-4 rounded-full bg-[#e11d48] hover:bg-[#be123c] disabled:opacity-60 font-semibold flex items-center justify-center gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {preview ? "Simulate Booking" : "Submit Booking Request"}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
